// packages/sync-engine — corrected r4-compliant client, revised r4.1 per Codex
// Session B correction §2. Pure Node, zero deps. See ADR-004 / ADR-013.
'use strict';

export const DEFAULT_OPTS = Object.freeze({
  pullLimit: 500,
  pullPagesPerTick: 8,
  pushBatch: 50,
  maxRetries: 5, // enforced by the engine (see submit()/flush())
  retryBaseMs: 2000,
  retryCapMs: 15 * 60 * 1000,
  retryJitter: 0.3,
});

// Engine states — sticky failure states (conflict/failed/auth_required) are
// NOT overwritten by 'idle' when the outbox empties.
const STICKY = new Set(['conflict', 'failed', 'auth_required']);

export class SyncEngine {
  constructor(deps) {
    if (!deps || !deps.storage || !deps.transport) {
      throw new Error('SyncEngine requires storage + transport adapters');
    }
    this.storage = deps.storage;
    this.transport = deps.transport;
    this.now = deps.now || (() => Date.now());
    this.sleep = deps.sleep || ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.opts = { ...DEFAULT_OPTS, ...(deps.options || {}) };
    this.state = 'idle';
    this.listeners = new Set();
    this.pullInFlight = new Map();
    this.pushInFlight = false;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  _set(state) {
    // Sticky-state rule: never demote to 'idle' silently. Downgrade to 'idle'
    // requires the caller to clear the sticky flag explicitly via clearSticky().
    if (state === 'idle' && STICKY.has(this.state)) return;
    if (state !== this.state) {
      this.state = state;
      for (const fn of this.listeners) fn(state);
    }
  }
  clearSticky() {
    // User acknowledged a conflict / rejected / auth_required. Transition to idle.
    if (STICKY.has(this.state)) {
      this.state = 'idle';
      for (const fn of this.listeners) fn('idle');
    }
  }

  async submit(envelope) {
    await this.storage.enqueue({ ...envelope, attempts: 0, next_attempt_at: this.now() });
    await this.flush();
  }

  /**
   * H10-R2: read-only view of pending outbox envelopes. Used by the mobile
   * canonical-plus-pending projection helper so screens can render optimistic
   * state after a process restart. Delegates directly to the shared
   * StorageAdapter — no second queue.
   */
  async pendingOutbox() {
    return this.storage.outbox();
  }

  async flush() {
    if (this.pushInFlight) return;
    this.pushInFlight = true;
    try {
      let box = await this.storage.outbox();
      if (!box.length) {
        // No work — do NOT clobber a sticky state (conflict / failed /
        // auth_required). Only transition to idle if we're not sticky.
        if (!STICKY.has(this.state)) this._set('idle');
        return;
      }
      this._set('pushing');

      const ready = (m) => (m.next_attempt_at || 0) <= this.now();
      let anyRetryable = false;

      for (let i = 0; i < box.length && !anyRetryable; i += this.opts.pushBatch) {
        const batch = box.slice(i, i + this.opts.pushBatch).filter(ready);
        if (!batch.length) continue;
        let results;
        try {
          results = await this.transport.push(batch);
        } catch (err) {
          // Network / transport failure — leave the outbox untouched.
          this._set('offline');
          throw err;
        }
        if (!Array.isArray(results) || results.length !== batch.length) {
          throw new Error('sync-engine: push returned wrong result count');
        }
        for (let k = 0; k < batch.length; k++) {
          const m = batch[k];
          const r = results[k];
          if (!r || typeof r !== 'object' || typeof r.result !== 'string') {
            throw new Error('sync-engine: malformed mutation result');
          }
          if (r.result === 'saved') {
            await this.storage.dequeue(m.mutation_id);
          } else if (r.result === 'conflict') {
            await this.storage.dequeue(m.mutation_id);
            await this.storage.recordConflict(m, r.canonical_entity);
            this._set('conflict');
          } else if (r.result === 'rejected') {
            await this.storage.dequeue(m.mutation_id);
            if (r.error && r.error.code === 'SITE_REVOKED') {
              await this.storage.purgeSite(m.site_id, {
                keepRedactedNotifications: true,
                reason: 'SITE_REVOKED',
              });
            }
            this._set('failed');
          } else if (r.result === 'retryable') {
            // §2.1: a retryable result STOPS the flush — later batches are
            // NOT sent this tick. Increment attempts + set next_attempt_at
            // with exponential backoff. Terminal after opts.maxRetries.
            const attempts = (m.attempts || 0) + 1;
            if (attempts >= this.opts.maxRetries) {
              await this.storage.dequeue(m.mutation_id);
              await this.storage.recordConflict(m, {
                exhausted: true,
                last_error: r.error || null,
              });
              this._set('failed');
            } else {
              const base = this.opts.retryBaseMs * Math.pow(2, attempts - 1);
              const capped = Math.min(base, this.opts.retryCapMs);
              const jitter = capped * this.opts.retryJitter * (Math.random() - 0.5) * 2;
              await this.storage.updateMutation(m.mutation_id, {
                attempts,
                next_attempt_at: this.now() + capped + jitter,
                last_error: r.error || null,
              });
              this._set('recovery');
            }
            anyRetryable = true;
            break;
          }
        }
      }

      box = await this.storage.outbox();
      const pending = box.filter((m) => (m.attempts || 0) < this.opts.maxRetries);
      if (!anyRetryable && pending.length === 0) this._set('idle');
    } finally {
      this.pushInFlight = false;
    }
  }

  async pull(site_id) {
    if (this.pullInFlight.get(site_id)) return this.pullInFlight.get(site_id);
    const p = (async () => {
      this._set('pulling');
      let pagesThisTick = 0;
      let lastKnown = await this.storage.getCheckpoint(site_id);
      for (;;) {
        const after = lastKnown;
        let resp;
        try {
          resp = await this.transport.pull(site_id, after, this.opts.pullLimit);
        } catch (err) {
          // §2.3: pull transport failure MUST NOT leave state at 'pulling'.
          // Enter offline; do not modify storage.
          this._set('offline');
          throw err;
        }

        // §2.4: validate the envelope BEFORE modifying storage.
        if (!resp || typeof resp !== 'object') {
          this._set('recovery');
          throw new Error('sync-engine: malformed pull response');
        }
        if (resp.site_id !== site_id) {
          this._set('recovery');
          throw new Error(
            'sync-engine: pull site_id mismatch (got ' + resp.site_id + ' want ' + site_id + ')',
          );
        }
        if (typeof resp.next_after !== 'number' || resp.next_after < after) {
          this._set('recovery');
          throw new Error(
            'sync-engine: pull next_after regressed (' + resp.next_after + ' < ' + after + ')',
          );
        }
        if (typeof resp.has_more !== 'boolean') {
          this._set('recovery');
          throw new Error('sync-engine: pull has_more not a boolean');
        }
        if (!Array.isArray(resp.changes) || resp.changes.length > this.opts.pullLimit) {
          this._set('recovery');
          throw new Error('sync-engine: pull returned invalid changes array');
        }

        // §2.5: atomic apply + checkpoint via storage adapter contract.
        await this.storage.applyPageAndAdvance(site_id, resp.changes, resp.next_after);
        try {
          await this.transport.advanceCheckpoint(site_id, resp.next_after);
        } catch (_) {
          /* best-effort */
        }
        lastKnown = resp.next_after;

        pagesThisTick += 1;
        if (!resp.has_more) break;
        if (pagesThisTick >= this.opts.pullPagesPerTick) break;
      }
      if (this.state === 'pulling') this._set('idle');
    })().finally(() => this.pullInFlight.delete(site_id));
    this.pullInFlight.set(site_id, p);
    return p;
  }

  async onSiteRevoked(site_id) {
    // §2.6: purge entities, checkpoint, outbox entries AND conflict records
    // scoped to this site; retain only redacted mutation notifications.
    await this.storage.purgeSite(site_id, {
      keepRedactedNotifications: true,
      reason: 'SITE_REVOKED',
    });
  }
}

// ---- In-memory adapter used by unit tests ----
export function createInMemoryStorage() {
  const outbox = new Map();
  const checkpoints = new Map();
  const entities = new Map();
  const conflicts = [];
  const redactedNotifications = [];
  const purged = new Set();

  return {
    async enqueue(m) {
      outbox.set(m.mutation_id, m);
    },
    async dequeue(id) {
      outbox.delete(id);
    },
    async updateMutation(id, patch) {
      const cur = outbox.get(id);
      if (cur) outbox.set(id, { ...cur, ...patch });
    },
    async outbox() {
      return Array.from(outbox.values());
    },
    async getCheckpoint(site_id) {
      return checkpoints.get(site_id) ?? 0;
    },
    async setCheckpoint(site_id, seq) {
      checkpoints.set(site_id, seq);
    },
    async applyPageAndAdvance(site_id, snapshots, next_after) {
      // Atomic: in memory, we simply do both in a single microtask.
      const arr = entities.get(site_id) || [];
      for (const s of snapshots) arr.push(s);
      entities.set(site_id, arr);
      checkpoints.set(site_id, next_after);
    },
    async recordConflict(envelope, snapshot) {
      conflicts.push({ envelope, snapshot });
    },
    async purgeSite(site_id, opts = {}) {
      purged.add(site_id);
      entities.delete(site_id);
      checkpoints.delete(site_id);
      // Remove outbox entries for the revoked site; retain redacted notification.
      for (const [id, m] of outbox) {
        if (m.site_id === site_id) {
          outbox.delete(id);
          if (opts.keepRedactedNotifications) {
            redactedNotifications.push({
              mutation_id: id,
              revoked_at: new Date().toISOString(),
              reason: opts.reason || 'SITE_REVOKED',
            });
          }
        }
      }
      // Remove conflict records scoped to this site.
      for (let i = conflicts.length - 1; i >= 0; i--) {
        if (conflicts[i].envelope && conflicts[i].envelope.site_id === site_id)
          conflicts.splice(i, 1);
      }
    },
    _entities: entities,
    _conflicts: conflicts,
    _purged: purged,
    _outbox: outbox,
    _checkpoints: checkpoints,
    _redactedNotifications: redactedNotifications,
  };
}
